import React, {useEffect, useState} from 'react'
import {Dispatch} from 'redux'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, useNavigate} from 'react-router-dom'
import * as auth_actions from '../../../../setup/redux/auth/actions'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {RootState} from '../../../../setup'
import {Loader} from '../../../services/Loader'
import {
  AuthFooter,
  decryptPassword,
  encryptPassword,
  paymentLink,
  checkUpgradePaymentRequired,
  isCompanyGracePeriodExpired,
} from '../../../services/Functional'
import Cookies from 'js-cookie'
import {AuthSidePanel} from './AuthSidePanel'
// import {postRequest} from '../../../../setup/redux/common/cruds'
import {endCall} from '../../../../setup/redux/common/slices'
import {faEye, faEyeSlash, faUser, faLock} from '@fortawesome/free-solid-svg-icons'
import FAIcon from '../../../../_metronic/helpers/components/FAIcon'
import {AcceptInviteDialog} from './dialogs/AcceptInviteDialog'

const loginSchema = Yup.object().shape({
  username: Yup.string().email('Invalid email format.').required('Email is required'),
  password: Yup.string().required('Password is required'),
})

export function Login() {
  const initialValues = {
    username: Cookies.get('username'),
    password: Cookies.get('password'),
  }
  let navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [rememberMe, setRememberMe] = useState<boolean>(false)
  const [passwordType, setPasswordType] = useState<string>('password')
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const {
    actionsLoading,
    config,
    user,
    isOTPMode,
    isOTPSentMode,
    isInOTPPage,
    temporaryPassword,
  }: any = useSelector<RootState>(
    (state) => ({
      actionsLoading: state.common.actionsLoading,
      config: state.configuration.configurations,
      user: state.auth.user,
      access_token: state.auth.access_token,
      isOTPMode: state.auth.isOTPMode,
      isOTPSentMode: state.auth.isOTPSentMode,
      isInOTPPage: state.auth.isInOTPPage,
      temporaryPassword: state.auth.temporaryPassword,
    }),
    shallowEqual
  )

  React.useEffect(() => {
    if (Cookies.get('username') && Cookies.get('password')) {
      setRememberMe(true)
    }
  }, [])

  React.useEffect(() => {
    dispatch(auth_actions.handleDeleteData())
    dispatch(auth_actions.handleDeleteUser())
    dispatch(auth_actions.setIsOTPMode(false))
    dispatch(auth_actions.handleIsInOTPPage(false))
    localStorage.setItem('otpRemainingTime', '30')
    localStorage.setItem('otpResendCount', '0')
    localStorage.setItem('otpMaxResendCount', '3')
    dispatch(auth_actions.handlePhoneNoVerifyDialog(false))
    dispatch(auth_actions.handleEmailVerifyDialog(false))
    dispatch(auth_actions.handleIsExpiredCompanyAPIcalling(false))
    dispatch(auth_actions.handleExpiredCompany(null))
  }, [])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      if (!isOTPMode) {
        if (rememberMe) {
          if (values.username && values.password) {
            Cookies.set('username', values.username, {expires: 365})
            Cookies.set('password', values.password, {expires: 365})
          }
        } else {
          Cookies.remove('username')
          Cookies.remove('password')
        }
        let response: any = await dispatch(auth_actions.login(values))
        switch (true) {
          case response.data.status === 400 ||
            (!response.data.success && response.data.code === 403):
            dispatch(endCall({}))
            setHasErrors(true)
            setMessage(response.data.message)
            setTimeout(() => {
              setHasErrors(false)
              setMessage('')
            }, 5000)
            break

          case !response.data.success && response.data.code === 401:
            dispatch(endCall({}))
            setShowConfirmationDialog(true)
            return

          case response.data.payment_status === 'pending' && response.data.type === 'paid':
            return window.location.replace(response.data.paymentLink)

          case response.status === 200 || response.status === 201:
            setHasErrors(false)
            dispatch(auth_actions.handleManageUserData(response))
            let inspectionCompanies: any = await dispatch(auth_actions.getInspectionCompanies())
            const isCompanyAdmin = inspectionCompanies.data.data.inspection_companies?.some(
              (company: any) =>
                company.groups?.some((group: any) => group.slug === 'company-admins')
            )
            if (!response.data.data.user.is_root && !isCompanyAdmin) {
              dispatch(endCall({}))
              setShowConfirmationDialog(true)
            } else if (inspectionCompanies.data.data.inspection_companies.length > 1) {
              if (response.data.data.user.is_two_factor_auth) {
                dispatch(auth_actions.setIsOTPMode(true))
                if (!rememberMe) {
                  const encryptedPassword = encryptPassword(formik?.values?.password)
                  dispatch(auth_actions.handleTemporaryPassword(encryptedPassword))
                }
                dispatch(endCall({}))
              } else {
                await dispatch(auth_actions.getUserAccess())
                dispatch(endCall({}))
                navigate('/company-select')
              }
            } else {
              if (response.data.data.user.is_root) {
                if (response.data.data.user.is_two_factor_auth) {
                  dispatch(auth_actions.setIsOTPMode(true))
                  if (!rememberMe) {
                    const encryptedPassword = encryptPassword(formik?.values?.password)
                    dispatch(auth_actions.handleTemporaryPassword(encryptedPassword))
                  }
                  dispatch(endCall({}))
                } else {
                  await dispatch(auth_actions.getUserAccess())
                  dispatch(endCall({}))
                  navigate('/dashboard')
                }
              } else {
                const selectedCompany = inspectionCompanies.data.data.inspection_companies[0]
                const groupIds = selectedCompany.groups.map((group: any) => group.slug)
                if (groupIds.includes('company-admins')) {
                  const expiryData: any = await dispatch(
                    auth_actions.checkSubscriptionExpiry(selectedCompany.id)
                  )
                  const upgradePaymentData = checkUpgradePaymentRequired(selectedCompany)
                  const gracePeriodExpiredCompany = isCompanyGracePeriodExpired(selectedCompany)
                  switch (true) {
                    case selectedCompany.plans[0].subscription.type === 'paid' &&
                      selectedCompany.plans[0].payment_status === 'pending':
                      dispatch(endCall({}))
                      return (window.location.href = paymentLink(
                        selectedCompany.plans[0].subscription_type === 'monthly'
                          ? selectedCompany.plans[0].subscription.payment_link.monthly
                          : selectedCompany.plans[0].subscription.payment_link.yearly,
                        selectedCompany.email,
                        selectedCompany.plans[0].reference_id
                      ))

                    case !gracePeriodExpiredCompany &&
                      !selectedCompany.is_expired &&
                      selectedCompany.plans[0].payment_status === 'paid' &&
                      !selectedCompany.is_active:
                      setHasErrors(true)
                      setMessage(response.data.message)
                      dispatch(endCall({}))
                      setTimeout(() => {
                        setHasErrors(false)
                      }, 5000)
                      break

                    case expiryData?.data?.inspection_company &&
                      expiryData.data.inspection_company <= 0 &&
                      gracePeriodExpiredCompany &&
                      upgradePaymentData.required:
                      dispatch(endCall({}))
                      return (window.location.href = upgradePaymentData.link!)

                    case expiryData?.data?.inspection_company &&
                      expiryData.data.inspection_company <= 0 &&
                      gracePeriodExpiredCompany:
                      dispatch(endCall({}))
                      dispatch(auth_actions.handleIsExpiredCompanyAPIcalling(true))
                      dispatch(auth_actions.handleExpiredCompany(selectedCompany))
                      navigate('/error/access-denied')
                      break

                    case !gracePeriodExpiredCompany &&
                      !selectedCompany.is_expired &&
                      !selectedCompany.is_active:
                      setHasErrors(true)
                      setMessage(response.data.message)
                      dispatch(endCall({}))
                      setTimeout(() => {
                        setHasErrors(false)
                      }, 5000)
                      break

                    case response.data.data.user.is_two_factor_auth:
                      dispatch(auth_actions.setIsOTPMode(true))
                      if (!rememberMe) {
                        const encryptedPassword = encryptPassword(formik?.values?.password)
                        dispatch(auth_actions.handleTemporaryPassword(encryptedPassword))
                      }
                      dispatch(endCall({}))
                      break

                    default:
                      await dispatch(auth_actions.getUserAccess())
                      dispatch(endCall({}))
                      navigate('/dashboard')
                      break
                  }
                } else {
                  dispatch(endCall({}))
                  navigate('/error/auth')
                }
              }
            }
            break

          default:
            setHasErrors(true)
            setMessage(response.message)
            setTimeout(() => {
              setHasErrors(false)
            }, 5000)
            break
        }
      }
    },
  })

  useEffect(() => {
    if (!rememberMe) {
      if (user?.email && temporaryPassword) {
        const decryptedPassword = decryptPassword(temporaryPassword)
        formik.setFieldValue('username', user?.email)
        formik.setFieldValue('password', decryptedPassword)
      }
    }
  }, [user])

  // useEffect(() => {
  //   if (access_token) {
  //     navigate('/dashboard')
  //   }
  // }, [user])

  useEffect(() => {
    const handleNavigation = () => {
      if (!isInOTPPage) {
        navigate('/auth/login')
      }
    }
    handleNavigation()
    window.addEventListener('pageshow', handleNavigation)
  }, [isInOTPPage])

  const handleOTPMode = async (OTPMode: string) => {
    const formattedPhoneNumber = `+${user?.country_code}${user?.phone}`
    let values
    if (OTPMode === 'mobile') {
      values = {
        mobile_no: formattedPhoneNumber,
        mode: 'mobile',
      }
    } else {
      values = {
        email: user?.email,
        mode: 'email',
      }
    }
    let response: any = await dispatch(auth_actions.handleOTPMode(values))
    if (!response.data.success && response.data.code === 401) {
      navigate('/error/auth')
    } else if (response.data.code === 200 || response.data.code === 201) {
      setHasErrors(false)
      setMessage('')
      dispatch(auth_actions.handleIsOTPSentMode(true))
      dispatch(auth_actions.handleIsInOTPPage(true))
      navigate('/auth/otp')
    } else {
      setHasErrors(true)
      setMessage(response.message)
      setTimeout(() => {
        setHasErrors(false)
        setMessage('')
      }, 5000)
    }
  }

  const handleBackbutton = () => {
    dispatch(auth_actions.resetData())
    if (!rememberMe) {
      formik.setFieldValue('username', '')
      formik.setFieldValue('password', '')
      dispatch(auth_actions.handleTemporaryPassword(undefined))
    }
  }

  const handleEnterKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isOTPMode && !isOTPSentMode) {
      event.preventDefault()
      formik.handleSubmit()
    }
  }

  return (
    <>
      {actionsLoading && <Loader />}
      <div className='row w-100 h-100 p-0 m-0'>
        <div className='col-md-7 p-0 bg-white position-relative'>
          <div className='py-5 d-flex justify-content-between container align-items-center mb-8'>
            <a href='https://decenthands.com/' className='cursor-pointer'>
              <img src={config && config?.actual_logo} alt='logo' width={90} />
            </a>
            {/* <Link to='https://decenthands.com/?page_id=4890' className='link-primary fs-6 float-end'>
              Contact Us
            </Link> */}
          </div>
          <div className='auth_form_container mx-auto d-flex align-items-center h-100'>
            <form
              className='h-100 login_auth_form auth_form'
              id='login_form'
              noValidate
              onSubmit={formik.handleSubmit}
            >
              <div className='mb-6 animate__animated animate__fadeInUp'>
                <h2 className='font-weight-bolder'>Sign In</h2>
                <div className='fs-5 text-muted'>Digitize Health & Safety</div>
              </div>
              {hasErrors && (
                <div className='mb-lg-5 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{message}</div>
                </div>
              )}
              <div className='mb-6 form-group animate__animated animate__fadeInUp'>
                <input
                  type='text'
                  tabIndex={1}
                  placeholder='mail@website.com'
                  autoComplete='off'
                  disabled={isOTPMode || isOTPSentMode}
                  {...formik.getFieldProps('username')}
                  className='form-control login_input'
                  onKeyDown={handleEnterKeyPress}
                />
                <FAIcon icon={faUser} className='left-icon' />
                {formik.touched.username && formik.errors.username && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.username}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='mb-6 form-group animate__animated animate__fadeInUp'>
                <input
                  type={passwordType}
                  tabIndex={2}
                  autoComplete='off'
                  placeholder='********'
                  disabled={isOTPMode || isOTPSentMode}
                  {...formik.getFieldProps('password')}
                  className='form-control login_input'
                  onKeyDown={handleEnterKeyPress}
                />
                <FAIcon icon={faLock} className='left-icon' />
                <FAIcon
                  icon={passwordType === 'password' ? faEyeSlash : faEye}
                  className='right-icon'
                  onClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
                />
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password}</span>
                    </div>
                  </div>
                )}
              </div>
              {isOTPMode || isOTPSentMode ? (
                <div className='d-flex flex-column'>
                  <div className='mb-6 form-group'>
                    <button
                      disabled={actionsLoading}
                      onClick={() => handleOTPMode('mobile')}
                      tabIndex={3}
                      className='btn btn-primary btn-pill animate__animated animate__fadeInUp w-100'
                    >
                      Send OTP on Mobile
                      <img
                        className='ms-3'
                        src={'/media/icons/duotune/general/Arrow - Right.svg'}
                        alt='->'
                      />
                    </button>
                  </div>
                  <div className='form-group'>
                    <button
                      disabled={actionsLoading}
                      onClick={() => handleOTPMode('email')}
                      tabIndex={4}
                      className='btn email-link-btn btn-pill animate__animated animate__fadeInUp w-100'
                    >
                      Send OTP on Email
                      <img
                        className='ms-3'
                        src={'/media/icons/duotune/general/blue-arrow.svg'}
                        alt='->'
                      />
                    </button>
                  </div>
                  <div className='d-flex align-items-center justify-content-center login-back-btn'>
                    <a
                      className='link-primary fs-6 animate__animated animate__fadeInUp w-100 text-center'
                      onClick={() => handleBackbutton()}
                      tabIndex={5}
                    >
                      <i className='bi bi-chevron-left me-1 link-primary'></i>
                      Back to login
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className='fv-row d-flex justify-content-between animate__animated animate__fadeInUp'>
                    <div className='mb-6 form-group d-flex vertical-align-middle'>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={rememberMe}
                          onChange={(e) => {
                            setRememberMe(e.target.checked)
                          }}
                        />
                      </div>
                      <label htmlFor='rememberMe mb-0' className='remember-me-text'>
                        Remember me &nbsp;
                      </label>
                    </div>
                    <Link to='/auth/forgot-password' className='link-primary fs-6 float-end'>
                      Forgot Password ?
                    </Link>
                  </div>
                  <button
                    type='submit'
                    disabled={actionsLoading}
                    tabIndex={3}
                    className='btn btn-primary btn-pill animate__animated animate__fadeInUp w-100'
                  >
                    Continue
                    <img
                      className='ms-3'
                      src={'/media/icons/duotune/general/Arrow - Right.svg'}
                      alt='->'
                    />
                  </button>
                  <div className='d-flex align-items-center justify-content-center mt-6 mb-3 gap-1 signUpLink'>
                    <label htmlFor='signUp' className='mb-0'>
                      Not a member yet?{' '}
                    </label>
                    <Link to='/auth/sign-up' className='link-primary fs-6'>
                      Sign Up
                    </Link>
                  </div>
                </>
              )}
            </form>
          </div>
          <div className='d-flex flex-column justify-content-center align-items-center w-100'>
            <AuthFooter />
          </div>
        </div>
        <div className='col-md-5 p-0 d-none d-md-flex lockbackground_card overflow-hidden position-relative'>
          <AuthSidePanel />
        </div>
      </div>
      {showConfirmationDialog && (
        <AcceptInviteDialog
          groups={['Supervisor', 'H&S Specialist']}
          showConfirmationDialog={true}
          isFirstStepCompleted={true}
        />
      )}
    </>
  )
}
